import React, {
    useState, useRef, useCallback, forwardRef, useImperativeHandle,
} from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.css';
import './HandsontableDark.css';
import {
    Box, Button, Typography, Tooltip,
} from '@mui/material';
import {
    Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import GuidedEntry from './GuidedEntry';

// ---------------------------------------------------------------------------
// Table mode helper
// ---------------------------------------------------------------------------

function buildTableData(count) {
    return Array.from({ length: count }, (_, i) => [i + 1, null]);
}

// Coerce a form-field string/number to a finite number, or null when blank/invalid.
// Blank string must be rejected explicitly — Number('') is 0, not NaN, which would
// otherwise be read as a real (zero) reference value.
function toFiniteNumberOrNull(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Type1DataEntryTable = forwardRef(function Type1DataEntryTable(
    {
        initialRows = 30, description = '', tolerance = '', referenceValue = '', k = '',
    },
    ref,
) {
    const hotRef = useRef(null);
    const [mode, setMode]             = useState('table');   // 'table' | 'guided'
    const [tableData, setTableData]   = useState(() => buildTableData(initialRows));
    const [guidedMeas, setGuidedMeas] = useState([]);

    // Expose interface to parent
    useImperativeHandle(ref, () => ({
        getValues: () => {
            if (mode === 'guided') return [...guidedMeas];
            if (!hotRef.current) return [];
            const hot = hotRef.current.hotInstance;
            const rows = hot.getData();
            return rows
                .map(row => row[1])
                .filter(v => v !== null && v !== '' && isFinite(Number(v)))
                .map(Number);
        },
        count: mode === 'guided'
            ? guidedMeas.length
            : (hotRef.current
                ? hotRef.current.hotInstance.getData().filter(r => r[1] !== null && r[1] !== '').length
                : 0),
    }));

    // GuidedEntry callback: appendMode so slotIndex === guidedMeas.length at call time
    const handleSubmitValue = useCallback((val) => {
        setGuidedMeas(prev => [...prev, val]);
    }, []);

    const handleEnterGuided = () => {
        // Seed guided list from table data so existing entries are preserved
        if (hotRef.current) {
            const hot = hotRef.current.hotInstance;
            const seeded = hot.getData()
                .map(r => r[1])
                .filter(v => v !== null && v !== '' && isFinite(Number(v)))
                .map(Number);
            setGuidedMeas(seeded);
        }
        setMode('guided');
    };

    const handleExitGuided = () => {
        // Write guided values back into table rows
        const rows = guidedMeas.map((v, i) => [i + 1, v]);
        const padded = rows.length < initialRows
            ? [...rows, ...buildTableData(initialRows - rows.length).map((r, i) => [rows.length + i + 1, null])]
            : rows;
        setTableData(padded);
        setMode('table');
    };

    if (mode === 'guided') {
        // Recomputed every render from the (possibly-changing) tolerance/referenceValue
        // props — not seeded once at mount — so editing the study form while the
        // overlay is open updates the pinned chips/run-chart lines immediately.
        const refNum = toFiniteNumberOrNull(referenceValue);
        const tolNum = toFiniteNumberOrNull(tolerance);

        const contextChips = [];
        if (referenceValue !== '' && referenceValue !== null && referenceValue !== undefined) {
            contextChips.push({ label: 'Reference', value: referenceValue });
        }
        if (tolerance !== '' && tolerance !== null && tolerance !== undefined) {
            contextChips.push({ label: 'Tolerance', value: tolerance });
        }

        // Minitab's Type 1 Gage Study run-chart band: Ref +/- k*Tol/2, where k is
        // the percent-of-tolerance evaluation fraction (k=0.2 default = +/-10% of
        // tolerance). This is deliberately narrower than the full +/-Tol/2 spec
        // band (~5x wider) — a passing study's spread is a small fraction of the
        // full tolerance, so the full band flattens the trace via Chart.js
        // auto-scaling and hides drift. Only draw the band when ref/tol/k are all
        // well-defined finite numbers with tol>0 and k>0; k falls back to 0.2
        // (matching the form default) when absent. Do not also pass the
        // full-tolerance lines — they would dominate the axis scale.
        const kProvided = k !== '' && k !== null && k !== undefined;
        const kEff = kProvided ? toFiniteNumberOrNull(k) : 0.2;
        const specLimits = (refNum !== null && tolNum !== null && tolNum > 0 && kEff !== null && kEff > 0)
            ? {
                usl: refNum + (kEff * tolNum) / 2,
                lsl: refNum - (kEff * tolNum) / 2,
                target: refNum,
                uslLabel: 'Ref + k·Tol/2',
                lslLabel: 'Ref − k·Tol/2',
                targetLabel: 'Reference',
            }
            : null;

        return (
            <GuidedEntry
                contextLabel={description || 'Type 1 GRR — Guided Entry'}
                slots={[]}
                values={guidedMeas}
                onSubmitValue={handleSubmitValue}
                onClose={handleExitGuided}
                appendMode
                contextChips={contextChips}
                specLimits={specLimits}
            />
        );
    }

    const valueCount = tableData.filter(r => r[1] !== null && r[1] !== '').length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
            {/* Mode toggle + count */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem' }}>
                    {valueCount} measurement{valueCount === 1 ? '' : 's'} entered
                </Typography>
                <Tooltip title="Switch to guided fullscreen entry (keyboard wedge / mobile)">
                    <Button
                        size="small"
                        startIcon={<FullscreenIcon />}
                        onClick={handleEnterGuided}
                        sx={{ fontSize: '0.7rem', color: 'rgba(144,202,249,0.7)', py: 0.25, px: 1 }}
                    >
                        Guided Entry
                    </Button>
                </Tooltip>
            </Box>

            {/* Handsontable */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 200, maxHeight: 420 }}>
                <HotTable
                    ref={hotRef}
                    data={tableData}
                    colHeaders={['#', 'Measurement']}
                    columns={[
                        { data: 0, type: 'numeric', readOnly: true },
                        { data: 1, type: 'numeric', numericFormat: { pattern: '0.0000' } },
                    ]}
                    rowHeaders={false}
                    height="100%"
                    width="100%"
                    stretchH="all"
                    licenseKey="non-commercial-and-evaluation"
                    afterChange={(changes) => {
                        if (!changes) return;
                        const hot = hotRef.current?.hotInstance;
                        if (!hot) return;
                        // Auto-add row if last row is filled
                        const lastRow = hot.countRows() - 1;
                        const lastVal = hot.getDataAtCell(lastRow, 1);
                        if (lastVal !== null && lastVal !== '') {
                            hot.alter('insert_row_below', lastRow);
                            hot.setDataAtCell(lastRow + 1, 0, lastRow + 2);
                        }
                    }}
                />
            </Box>
        </Box>
    );
});

export default Type1DataEntryTable;
